"use client";

import React from "react";
import { ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, Award, Building2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface AccreditationItem {
  id: string;
  name: string;
  shortName: string;
  status: string;
  description: string;
  standards: string[];
  scope: string;
}
const accreditations: AccreditationItem[] = [{
  id: "nasc",
  name: "National Access & Scaffolding Confederation",
  shortName: "NASC",
  status: "Full Audited Member",
  description: "The national trade body for access and scaffolding in the UK. NASC membership requires rigorous biennial audits of safety policies, training records, site inspections, and structural compliance.",
  standards: ["SG4:22 Preventing Falls in Scaffolding", "TG20:21 Good Practice for Tube and Fitting Scaffolding", "CISRS qualified operatives on site", "Independent biennial criteria audits"],
  scope: "Commercial, industrial, and complex temporary works"
}, {
  id: "chas",
  name: "Contractors Health and Safety Assessment Scheme",
  shortName: "CHAS",
  status: "Accredited Contractor",
  description: "Verified compliance against SSIP (Safety Schemes in Procurement) core criteria. Assesses our health and safety management, risk assessments, method statements, and operational safeguards.",
  standards: ["SSIP core criteria full compliance", "Standardised site risk management", "Annual independent safety reviews", "Documented RAMS for every site install"],
  scope: "Site safety management, RAMS, and workforce protection"
}];
const safetyMetrics = [{
  title: "100% CISRS carded",
  detail: "Every scaffolder carries a valid, checked CISRS card."
}, {
  title: "TG20:21 compliance",
  detail: "Calculated structural design sheets provided with every build."
}, {
  title: "Dedicated site audits",
  detail: "Weekly handover certificates and 7-day inspection tagging."
}, {
  title: "Direct accountability",
  detail: "Direct oversight on all South East commercial and domestic sites."
}];
export default function Accreditations() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.075,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="accreditations-certifications" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header Block with Structural Datum Rule */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="w-12 h-1 bg-primary mb-4" />
          <span className="text-xs md:text-sm font-bold uppercase tracking-wider text-muted-foreground block mb-2">
            Safety & Standards
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Verified scaffolding accreditations
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            We hold formal accreditations from the UK&apos;s leading safety and procurement bodies. Every BMF project runs under audited site procedures, qualified CISRS operatives, and rigorous risk assessments.
          </p>
        </div>

        {/* Accreditations Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16 max-w-5xl mx-auto">
          {accreditations.map((acc, index) => <motion.div key={acc.id} variants={itemVariants} data-index={index}>
              <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md hover:border-primary/50 transition-[border-color,box-shadow] rounded-lg">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-muted text-foreground text-xs font-semibold">
                        <Award className="size-3.5 text-primary" />
                        <span>{acc.shortName}</span>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-chart-4 bg-muted px-2 py-0.5 rounded">
                        <CheckCircle2 className="size-3 text-chart-4" />
                        {acc.status}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground font-default">
                      {acc.name}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {acc.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-0">
                    <div className="border-t border-border pt-4">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-foreground mb-3">
                        Audited Standards & Controls
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        {acc.standards.map((standard, sIndex) => <li key={sIndex} data-index={sIndex} className="flex items-start gap-2">
                            <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                            <span className="leading-snug">{standard}</span>
                          </li>)}
                      </ul>
                    </div>

                    <div className="p-3 bg-muted/60 rounded border border-border/60 text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground block mb-0.5">Application scope:</span>
                      {acc.scope}
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 border-t border-border flex items-center justify-end text-xs">
                  <span className="font-medium text-foreground">Verified</span>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Operational Safeguards Metric Strip */}
        <div className="bg-card border border-border rounded-lg p-6 sm:p-8 mb-12 shadow-xs">
          <div className="max-w-2xl mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-foreground font-default mb-1">
              Core site compliance standards
            </h3>
            <p className="text-sm text-muted-foreground">
              Applied on every commercial erection, residential build, and solar access platform.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {safetyMetrics.map((metric, mIndex) => <div key={mIndex} data-index={mIndex} className="border-l-2 border-primary pl-4 py-1">
                <p className="text-sm font-bold text-foreground mb-1">{metric.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{metric.detail}</p>
              </div>)}
          </div>
        </div>

        {/* Action Panel */}
        <div className="bg-secondary text-secondary-foreground rounded-lg p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border border-border">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Building2 className="size-5 text-primary" />
              <h3 className="text-lg sm:text-xl font-bold text-secondary-foreground font-default">
                Require safety compliance packs for your project tender?
              </h3>
            </div>
            <p className="text-sm text-secondary-foreground/80 max-w-2xl">
              We provide full insurance certificates, RAMS, NASC compliance docs, and CISRS operative cards upon request.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                <span>Request documentation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="default" className="border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10">
              <Link to="/ProjectsCaseStudiesPage">
                View completed projects
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}